/*
  Customer video row (sections/ugc-video-row.liquid). Cards are rendered by Liquid from the "UGC video"
  metaobjects; this script adds the looping spotlight carousel and playback. The centre video plays
  muted; tap it or its speaker for sound, tap a side card (or use the arrows / swipe) to bring it to the centre.
  Video loading: an MP4 sized to the connection (see pickFile); the next card preloads so moving to it
  starts at once.
*/
(() => {
  if (customElements.get('ugc-video-row')) return;

  const ICON_MUTED = '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M3 7.5h3l4-3.5v12l-4-3.5H3v-5Z" fill="currentColor"/><path d="m13 7.5 5 5m0-5-5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
  const ICON_SOUND = '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M3 7.5h3l4-3.5v12l-4-3.5H3v-5Z" fill="currentColor"/><path d="M13.5 7a4 4 0 0 1 0 6M15.5 5a7 7 0 0 1 0 10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
  const ICON_PAUSE = '<svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><rect x="5" y="4" width="3.5" height="12" rx="1"/><rect x="11.5" y="4" width="3.5" height="12" rx="1"/></svg>';
  const ICON_PLAY_SMALL = '<svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path d="M6 4v12l10-6-10-6Z"/></svg>';
  const VISIBLE_RANGE = 3; // cards up to 3 places from the centre stay rendered; the stage clips the rest
  const MILESTONES = [25, 50, 75, 100];

  // Video files: Shopify keeps each video in 480p, 720p and 1080p MP4s, listed in its stream index (m3u8,
  // about 1 KB). The theme only gets the index and the 1080p file, so the script reads the index and plays
  // the best MP4 the connection can carry, capped at 720p (cards are 258-300px wide). A plain MP4 starts
  // faster than a stream player and needs no extra script, in every browser including Safari.
  const connection = navigator.connection || {};
  const slow = Boolean(connection.saveData) || /(^|-)2g$|^3g$/.test(connection.effectiveType || '');
  const budget = () => (slow ? 1 : Math.min(8, (connection.downlink || 6) * 0.6)) * 1e6; // bits per second
  const MAX_HEIGHT = 720;

  const indexes = new Map();
  const readIndex = (url) => {
    if (!indexes.has(url)) {
      indexes.set(url, fetch(url).then((response) => (response.ok ? response.text() : '')).then((text) => {
        const base = url.split('?')[0].replace(/[^/]*$/, '');
        const lines = text.split('\n');
        const files = [];
        lines.forEach((line, i) => {
          if (!line.startsWith('#EXT-X-STREAM-INF')) return;
          const bandwidth = Number((line.match(/AVERAGE-BANDWIDTH=(\d+)/) || line.match(/BANDWIDTH=(\d+)/) || [])[1]);
          const height = Number((line.match(/RESOLUTION=\d+x(\d+)/) || [])[1]);
          // "<id>.SD-480p.0.9Mbps.hls.85412858_.m3u8" is served as "<id>.SD-480p-0.9Mbps-85412858.mp4"
          const name = (lines[i + 1] || '').trim().replace(/\.((?:SD|HD)-\d+p)\.([\d.]+Mbps)\.hls\.(\d+)_\.m3u8$/, '.$1-$2-$3.mp4');
          if (bandwidth && height && name.endsWith('.mp4')) files.push({ bandwidth, height, url: base + name });
        });
        return files.sort((x, y) => y.bandwidth - x.bandwidth);
      }).catch(() => []));
    }
    return indexes.get(url);
  };

  const pickFile = async (video, maxHeight = MAX_HEIGHT, minBudget = 0) => {
    const { hls, mp4 } = video.dataset;
    const files = hls ? await readIndex(hls) : [];
    const fitting = files.filter((file) => file.height <= maxHeight);
    if (!fitting.length) return mp4;
    const limit = slow ? budget() : Math.max(budget(), minBudget);
    return (fitting.find((file) => file.bandwidth <= limit) || fitting[fitting.length - 1]).url;
  };

  const resume = (video) => {
    if (video.ugcPlay) video.play().catch(() => {});
  };

  // Each attach/unload bumps video.ugcGen, so a file chosen after the card was unloaded is dropped.
  const setSource = async (video) => {
    const gen = video.ugcGen;
    const url = await pickFile(video);
    if (video.ugcGen !== gen || !url) return;
    video.src = url;
    resume(video);
  };

  // WebVTT text -> [{ start, end, text }] (cue settings and styling tags are ignored).
  const parseSubtitles = (text) => {
    const seconds = (stamp) => stamp.split(':').reduce((total, part) => total * 60 + parseFloat(part), 0);
    return text.replace(/\r/g, '').split(/\n\s*\n/).map((block) => {
      const lines = block.trim().split('\n');
      const timing = lines.findIndex((line) => line.includes('-->'));
      if (timing < 0) return null;
      const [start, end] = lines[timing].split('-->').map((part) => seconds(part.trim().split(/\s+/)[0]));
      const cueText = lines.slice(timing + 1).join('\n').replace(/<[^>]+>/g, '').trim();
      return cueText ? { start, end, text: cueText } : null;
    }).filter(Boolean);
  };

  // Turns subtitle cues into short on-screen phrases: 3-6 words and at most two short lines, breaking at
  // punctuation where possible, never inside a capitalised name ("Overnight Repair Serum") or after a small
  // linking word. Each word is timed by its share of its cue; a phrase stays up at least 0.9 s.
  const LINKING = new Set(['a', 'an', 'the', 'of', 'to', 'and', 'or', 'but', 'for', 'with', 'in', 'on', 'at', 'my', 'your', 'this', 'that', 'is', 'so', 'like', 'i', "i've", "i'm", 'it', "it's"]);
  const toPhrases = (cues, lineChars) => {
    const words = cues.flatMap((cue) => {
      const list = cue.text.replace(/\s+/g, ' ').trim().split(' ');
      const total = list.reduce((sum, w) => sum + w.length + 1, 0);
      let t = cue.start;
      return list.map((text) => {
        const start = t;
        t += (cue.end - cue.start) * ((text.length + 1) / total);
        return { text, start, end: t };
      });
    });
    const maxChars = lineChars * 2;
    const phrases = [];
    let current = [];
    const textOf = (list) => list.map((w) => w.text).join(' ');
    const capital = (w) => w && /^[A-Z0-9]/.test(w.text) && w.text.toLowerCase() !== 'i';
    words.forEach((word, i) => {
      const plain = word.text.toLowerCase().replace(/[^a-z']/g, '');
      const endsSentence = /[.?!]$/.test(word.text);
      // A lone sentence ending ("nights.") joins the phrase before it when that still fits.
      const prev = phrases[phrases.length - 1];
      if (!current.length && endsSentence && prev && textOf([...prev, word]).length <= maxChars && prev.length < 7) {
        prev.push(word);
        return;
      }
      current.push(word);
      const next = words[i + 1];
      if (!next) return;
      const endsClause = /[,:;]$/.test(word.text);
      const tooLong = current.length >= 6 || textOf([...current, next]).length > maxChars;
      const insideName = capital(word) && capital(next) && !/[.,?!:;]$/.test(word.text)
        && textOf([...current, next]).length <= maxChars + 6;
      const number = /^\d/.test(word.text);
      const canBreak = !LINKING.has(plain) && !insideName && !number;
      if ((endsSentence && current.length >= 2) || (endsClause && current.length >= 3) || (tooLong && canBreak)) {
        phrases.push(current);
        current = [];
      } else if (tooLong && current.length > 3) {
        // Would end on a linking word or number: carry the tail into the next phrase instead.
        const tail = [];
        while (current.length > 3) {
          const last = current[current.length - 1];
          const lastPlain = last.text.toLowerCase().replace(/[^a-z']/g, '');
          if (!LINKING.has(lastPlain) && !/^\d/.test(last.text)) break;
          tail.unshift(current.pop());
        }
        if (tail.length) {
          phrases.push(current);
          current = tail;
        } else if (current.length >= 8) {
          phrases.push(current);
          current = [];
        }
      }
    });
    if (current.length) phrases.push(current);
    return phrases.reduce((list, group) => {
      const phrase = { start: group[0].start, end: group[group.length - 1].end, text: textOf(group) };
      const prev = list[list.length - 1];
      const joinable = prev && prev.text.length + phrase.text.length < maxChars && !/[.?!]$/.test(prev.text);
      if (joinable && (phrase.end - phrase.start < 0.9 || group.length < 2)) {
        prev.end = phrase.end;
        prev.text = `${prev.text} ${phrase.text}`;
      } else {
        list.push(phrase);
      }
      return list;
    }, []).map((phrase, i, list) => ({ ...phrase, end: list[i + 1] ? Math.max(phrase.end, list[i + 1].start) : phrase.end }));
  };

  const track = (event, detail) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...detail });
  };

  class UgcVideoRow extends HTMLElement {
    connectedCallback() {
      if (this.cards) return;
      this.stage = this.querySelector('.ugc-row__stage');
      this.cards = [...this.stage.querySelectorAll('.ugc-row__card')];
      if (!this.cards.length) return;
      this.placement = this.dataset.placement || 'homepage';
      this.autoplay = !window.matchMedia('(prefers-reduced-motion: reduce)').matches && !connection.saveData;
      this.classList.toggle('is-static', !this.autoplay);
      this.inView = false;
      this.centre = Math.max(0, this.cards.findIndex((card) => card.classList.contains('is-centre')));
      this.sent = new Set(); // dedupes analytics events per page view

      this.cards.forEach((card, i) => this.bindCard(card, i));
      this.bindNav();
      this.layout(true);

      // Plays as soon as a sliver of the row is on screen, like Tolstoy; pauses once it has left.
      new IntersectionObserver(([entry]) => {
        this.inView = entry.isIntersecting;
        if (this.inView) this.playCentre('autoplay');
        else this.stopAll();
      }, { threshold: 0.1 }).observe(this);

      // Start fetching the first video early: when the row is within 2,500px, when the page has finished loading,
      // or 4 s after the script runs, whichever comes first (Tolstoy loads its videos on page load too). The
      // stream indexes of the first cards are tiny and read at the same time.
      let warmed = false;
      const warmUp = () => {
        if (warmed) return;
        warmed = true;
        warm.disconnect();
        [0, 1, -1].forEach((step) => {
          const n = this.cards.length;
          const hls = this.cards[(((this.centre + step) % n) + n) % n].querySelector('video').dataset.hls;
          if (hls) readIndex(hls);
        });
        if (this.autoplay) this.attach(this.cards[this.centre]);
      };
      const warm = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) warmUp();
      }, { rootMargin: '2500px 0px' });
      warm.observe(this);
      if (!slow) {
        const whenIdle = () => (window.requestIdleCallback ? requestIdleCallback(warmUp, { timeout: 1000 }) : setTimeout(warmUp, 0));
        if (document.readyState === 'complete') whenIdle();
        else window.addEventListener('load', whenIdle, { once: true });
        setTimeout(whenIdle, 4000);
      }
    }

    // Distance of card i from the centre, wrapped so the row loops: -n/2 .. n/2.
    offsetOf(i) {
      const n = this.cards.length;
      let d = (i - this.centre) % n;
      if (d > n / 2) d -= n;
      if (d < -n / 2) d += n;
      return d;
    }

    layout(initial = false) {
      this.cards.forEach((card, i) => {
        const d = this.offsetOf(i);
        const previous = Number(card.style.getPropertyValue('--offset'));
        // A card wrapping round to the other end jumps instead of sliding across the row.
        card.classList.toggle('no-transition', initial || Math.abs(d - previous) > 1);
        card.style.setProperty('--offset', d);
        card.classList.toggle('is-centre', d === 0);
        card.classList.toggle('is-away', Math.abs(d) > VISIBLE_RANGE);
        card.querySelector('.ugc-row__media').tabIndex = Math.abs(d) <= 1 ? 0 : -1;
        card.querySelector('.ugc-row__sound').tabIndex = d === 0 ? 0 : -1;
        card.querySelector('.ugc-row__pause').tabIndex = d === 0 ? 0 : -1;
        const tile = card.querySelector('.ugc-row__product');
        if (tile) tile.tabIndex = Math.abs(d) <= 1 ? 0 : -1;
        card.setAttribute('aria-hidden', Math.abs(d) > VISIBLE_RANGE ? 'true' : 'false');
        // Cards that moved well away stop downloading so the bandwidth goes to the centre.
        if (Math.abs(d) > 1) this.unload(card);
        else if (!initial && card.classList.contains('no-poster')) this.attach(card, 'metadata');
      });
    }

    // Moves card i to the centre. Called only from visitor actions, so the new video counts as chosen.
    go(i) {
      const n = this.cards.length;
      const next = ((i % n) + n) % n;
      if (next === this.centre) return;
      const keepFocus = Boolean(document.activeElement?.closest('.ugc-row__card') && this.contains(document.activeElement));
      this.stopAll();
      this.centre = next;
      this.layout();
      this.choose(this.cards[next]);
      this.playCentre('tap');
      this.querySelector('.ugc-row__status').textContent = `Video ${next + 1} of ${n}`;
      if (keepFocus) this.cards[next].querySelector('.ugc-row__media').focus({ preventScroll: true });
    }

    // From now on this video's watch time counts towards the progress milestones.
    choose(card) {
      if (card.chosen) return;
      card.chosen = true;
      card.watched = 0;
      card.lastTime = card.querySelector('video').currentTime;
    }

    bindCard(card, i) {
      const media = card.querySelector('.ugc-row__media');
      const video = card.querySelector('video');
      const sound = card.querySelector('.ugc-row__sound');
      sound.innerHTML = ICON_MUTED;
      const pause = card.querySelector('.ugc-row__pause');
      pause.innerHTML = ICON_PAUSE;
      const syncPause = () => {
        const paused = video.paused || !card.classList.contains('is-playing');
        pause.innerHTML = paused ? ICON_PLAY_SMALL : ICON_PAUSE;
        pause.setAttribute('aria-label', paused ? 'Play video' : 'Pause video');
        pause.setAttribute('aria-pressed', String(paused));
      };
      video.addEventListener('play', syncPause);
      video.addEventListener('pause', syncPause);
      pause.addEventListener('click', () => {
        if (video.paused || !video.ugcPlay) {
          card.userPaused = false;
          this.choose(card);
          this.play(card, 'tap');
        } else {
          card.userPaused = true;
          video.ugcPlay = false;
          video.pause();
          card.classList.remove('is-playing');
        }
        syncPause();
      });
      media.addEventListener('click', () => {
        if (i !== this.centre) {
          this.go(i);
          return;
        }
        this.choose(card);
        this.openPlayer(i, this.keyboardOpen);
        this.keyboardOpen = false;
      });
      media.addEventListener('keydown', (e) => {
        if (e.target !== media || (e.key !== 'Enter' && e.key !== ' ')) return;
        e.preventDefault();
        this.keyboardOpen = true;
        media.click();
      });
      sound.addEventListener('click', () => {
        this.choose(card);
        this.toggleSound(card);
      });
      card.querySelector('.ugc-row__product')?.addEventListener('click', () => {
        track('ugc_product_click', {
          ugc_video: card.dataset.name,
          ugc_placement: this.placement,
          ugc_product: card.querySelector('.ugc-row__product-name')?.textContent.trim(),
        });
      });
      // The start event fires on a real start (not on an attempt the browser blocked).
      video.addEventListener('playing', () => {
        if (!card.pendingTrigger) return;
        const trigger = card.pendingTrigger;
        card.pendingTrigger = null;
        const position = i + 1;
        this.trackOnce(`start:${position}:${trigger}`, 'ugc_video_start', {
          ugc_video: card.dataset.name,
          ugc_placement: this.placement,
          ugc_trigger: trigger,
          ugc_position: position,
        });
      });
      // Watch milestones for chosen videos: seconds actually watched since choosing, loops included.
      video.addEventListener('timeupdate', () => {
        if (!card.chosen || !video.duration) return;
        const now = video.currentTime;
        let step = now - card.lastTime;
        if (step < -0.5) step = video.duration - card.lastTime + now; // looped back to the start
        card.lastTime = now;
        if (step > 0 && step < 2) card.watched += step;
        const percent = (card.watched / video.duration) * 100;
        MILESTONES.forEach((milestone) => {
          if (percent + 1 < milestone) return;
          this.trackOnce(`progress:${i}:${milestone}`, 'ugc_video_progress', {
            ugc_video: card.dataset.name,
            ugc_placement: this.placement,
            ugc_percent: milestone,
          });
        });
      });
      video.addEventListener('seeked', () => { card.lastTime = video.currentTime; });

      // Subtitles for videos without captions burned in (UGC video > Subtitles), as short phrases in the same
      // white-box style as the burned-in captions. Checked every frame while playing so phrases land on time.
      const subtitles = card.querySelector('.ugc-row__subtitles');
      const caption = card.querySelector('.ugc-row__caption');
      if (subtitles && caption) {
        const cues = parseSubtitles(subtitles.content.textContent);
        card.ugcCues = cues;
        const transcript = card.querySelector('.ugc-row__transcript');
        if (transcript) transcript.textContent = `Transcript: ${cues.map((c) => c.text.replace(/\n/g, ' ')).join(' ')}`;
        let phrases = null;
        let shown = null;
        let frame = 0;
        const update = () => {
          if (!phrases) phrases = toPhrases(cues, card.getBoundingClientRect().width >= 290 ? 26 : 22);
          const t = video.currentTime;
          const phrase = card.classList.contains('is-centre') ? phrases.find((p) => t >= p.start && t < p.end) : null;
          if (phrase === shown) return;
          const swap = Boolean(shown && phrase);
          shown = phrase;
          if (phrase) {
            // The box hugs the text: measure the wrapped words, then size the box to them.
            caption.style.width = '';
            caption.innerHTML = '';
            const words = document.createElement('span');
            words.textContent = phrase.text;
            caption.appendChild(words);
            const padding = parseFloat(getComputedStyle(caption).paddingLeft) * 2;
            caption.style.width = `${Math.ceil(words.getBoundingClientRect().width + padding)}px`;
          }
          caption.classList.toggle('is-shown', Boolean(phrase));
          if (swap) {
            caption.classList.remove('is-swap');
            void caption.offsetWidth; // restart the crossfade
            caption.classList.add('is-swap');
          }
        };
        const loop = () => {
          update();
          if (!video.paused) frame = requestAnimationFrame(loop);
        };
        video.addEventListener('playing', () => {
          cancelAnimationFrame(frame);
          frame = requestAnimationFrame(loop);
        });
        video.addEventListener('pause', () => cancelAnimationFrame(frame));
        video.addEventListener('seeked', update);
        video.addEventListener('emptied', update);
      }
    }

    bindNav() {
      this.querySelectorAll('[data-dir]').forEach((btn) => {
        btn.addEventListener('click', () => this.go(this.centre + Number(btn.dataset.dir)));
      });
      this.addEventListener('keydown', (e) => {
        const step = { ArrowRight: 1, ArrowLeft: -1 }[e.key];
        if (!step || !e.target.closest('.ugc-row__nav, .ugc-row__frame')) return;
        e.preventDefault();
        this.go(this.centre + step);
      });
      // Swipe: the row follows the finger, then snaps to the nearest card.
      let start = null;
      let axis = null;
      const step = () => this.cards[0].getBoundingClientRect().width + parseFloat(getComputedStyle(this).getPropertyValue('--ugc-gap'));
      this.stage.addEventListener('touchstart', (e) => {
        start = { x: e.touches[0].clientX, y: e.touches[0].clientY, t: performance.now() };
        axis = null;
      }, { passive: true });
      this.stage.addEventListener('touchmove', (e) => {
        if (!start) return;
        const dx = e.touches[0].clientX - start.x;
        const dy = e.touches[0].clientY - start.y;
        if (!axis && Math.hypot(dx, dy) > 8) axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        if (axis !== 'x') return;
        this.stage.classList.add('is-dragging');
        this.stage.style.setProperty('--drag', `${dx}px`);
      }, { passive: true });
      const end = (e) => {
        if (!start) return;
        const touch = e.changedTouches && e.changedTouches[0];
        const dx = touch ? touch.clientX - start.x : 0;
        const fast = Math.abs(dx) / Math.max(1, performance.now() - start.t) > 0.4;
        start = null;
        if (axis !== 'x') return;
        let steps = Math.round(-dx / step());
        if (steps === 0 && (Math.abs(dx) > 40 || fast)) steps = dx < 0 ? 1 : -1;
        this.stage.classList.remove('is-dragging');
        this.stage.style.setProperty('--drag', '0px');
        if (steps) this.go(this.centre + steps);
      };
      this.stage.addEventListener('touchend', end, { passive: true });
      this.stage.addEventListener('touchcancel', end, { passive: true });

      // Trackpad: one horizontal two-finger swipe moves one card. The momentum tail of the same swipe keeps
      // the lock alive, so a new move needs a short pause first.
      let wheelSum = 0;
      let wheelLock = 0;
      this.stage.addEventListener('wheel', (e) => {
        if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
        e.preventDefault();
        const now = performance.now();
        if (now < wheelLock) {
          wheelLock = now + 200;
          wheelSum = 0;
          return;
        }
        wheelSum += e.deltaX;
        if (Math.abs(wheelSum) > 60) {
          this.go(this.centre + (wheelSum > 0 ? 1 : -1));
          wheelSum = 0;
          wheelLock = now + 450;
        }
      }, { passive: false });
    }

    // Gives a card its video source. Neighbours get it early so moving to them starts at once.
    attach(card, preload = 'auto') {
      const video = card.querySelector('video');
      if (video.ugcWanted) {
        if (preload === 'auto') video.preload = 'auto';
        return video;
      }
      video.ugcWanted = true;
      video.ugcGen = (video.ugcGen || 0) + 1;
      video.preload = preload;
      if (!video.ugcBound) {
        video.ugcBound = true;
        let spinnerTimer;
        card.showSpinner = () => {
          clearTimeout(spinnerTimer);
          spinnerTimer = setTimeout(() => card.classList.add('is-buffering'), 350);
        };
        card.hideSpinner = () => {
          clearTimeout(spinnerTimer);
          card.classList.remove('is-buffering');
        };
        video.addEventListener('playing', () => {
          card.hideSpinner();
          card.classList.add('has-frame');
        });
        // Neighbours preload only once this video has enough buffered, so they never slow it down.
        video.addEventListener('canplaythrough', () => {
          if (card.classList.contains('is-centre')) this.preloadNeighbours();
        });
        video.addEventListener('waiting', () => { if (!video.paused) card.showSpinner(); });
        video.addEventListener('pause', () => card.hideSpinner());
      }
      setSource(video);
      return video;
    }

    unload(card) {
      const video = card.querySelector('video');
      if (!video.ugcWanted) return;
      video.ugcWanted = false;
      video.ugcGen = (video.ugcGen || 0) + 1;
      video.ugcPlay = false;
      video.pause();
      video.removeAttribute('src');
      video.load();
      card.hideSpinner?.();
      card.classList.remove('has-frame', 'is-playing');
    }

    // The next card buffers its opening; the previous one only its first frame, to save data.
    preloadNeighbours() {
      if (slow || this.cards.length < 2) return;
      const n = this.cards.length;
      this.attach(this.cards[(this.centre + 1) % n], 'auto');
      if (n > 2) this.attach(this.cards[(this.centre - 1 + n) % n], 'metadata');
    }

    playCentre(trigger) {
      if (!this.inView || (!this.autoplay && trigger === 'autoplay')) return;
      if (trigger === 'autoplay' && this.cards[this.centre].userPaused) return;
      this.play(this.cards[this.centre], trigger);
    }

    play(card, trigger) {
      const video = this.attach(card);
      card.classList.add('is-playing');
      // A video the visitor brought to the centre counts as chosen, even if autoplay fires again afterwards.
      card.pendingTrigger = card.chosen ? 'tap' : trigger;
      video.ugcPlay = true;
      if (video.readyState < 3) card.showSpinner();
      // Without a source yet (file still being picked), setSource starts playback when it attaches.
      if (video.getAttribute('src')) {
        video.play().catch(() => {
          card.classList.remove('is-playing');
          card.hideSpinner();
        });
      }
    }

    stopAll() {
      this.cards.forEach((card) => {
        const video = card.querySelector('video');
        video.ugcPlay = false;
        card.pendingTrigger = null;
        if (!video.paused) video.pause();
        video.muted = true;
        this.setSoundIcon(card, true);
        card.hideSpinner?.();
        card.classList.remove('is-playing');
      });
    }

    toggleSound(card) {
      const video = card.querySelector('video');
      if (video.paused) this.play(card, 'tap');
      video.muted = !video.muted;
      this.setSoundIcon(card, video.muted);
      if (!video.muted) {
        this.trackOnce(`unmute:${this.cards.indexOf(card)}`, 'ugc_video_unmute', {
          ugc_video: card.dataset.name,
          ugc_placement: this.placement,
        });
      }
    }

    // Full-screen player (UgcPlayer below), opened from the centre card at the moment it had reached.
    openPlayer(i, fromKeyboard = false) {
      this.player = this.player || new UgcPlayer(this);
      const video = this.cards[i].querySelector('video');
      this.stopAll();
      this.player.open(i, video.currentTime || 0, fromKeyboard);
    }

    // Product shown on the player card: the card's own tile (homepage), else this page's product.
    productOf(card) {
      const tile = card.querySelector('.ugc-row__product');
      if (tile) {
        return {
          url: tile.href,
          title: tile.querySelector('.ugc-row__product-name')?.textContent.trim(),
          price: tile.querySelector('.ugc-row__product-price')?.textContent.trim(),
          image: tile.querySelector('img')?.currentSrc || tile.querySelector('img')?.src,
          onPage: false,
        };
      }
      const { productTitle, productPrice, productImage, productUrl } = this.dataset;
      if (!productTitle) return null;
      return { url: productUrl, title: productTitle, price: productPrice, image: productImage, onPage: true };
    }

    // After the player closes, the row continues from the last video watched there.
    resumeFrom(i) {
      if (i !== this.centre) {
        this.centre = i;
        this.layout();
      }
      this.playCentre('tap');
    }

    trackOnce(key, event, detail) {
      if (this.sent.has(key)) return;
      this.sent.add(key);
      track(event, detail);
    }

    setSoundIcon(card, muted) {
      const button = card.querySelector('.ugc-row__sound');
      button.innerHTML = muted ? ICON_MUTED : ICON_SOUND;
      button.setAttribute('aria-pressed', String(!muted));
    }
  }

  const ICON_CLOSE = '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="m5 5 10 10M15 5 5 15" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>';
  const ICON_UP = '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="m5 12 5-5 5 5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const ICON_DOWN = '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="m5 8 5 5 5-5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  /*
    Full-screen story player: opens with sound from the tapped video, swipe up/down (phones) or arrows,
    wheel and keys (desktop) for the next/previous video, plays on to the next one at the end, progress
    bar, captions for videos without burned-in ones, and a product card. Back button / Esc closes it.
  */
  class UgcPlayer {
    constructor(row) {
      this.row = row;
      this.cards = row.cards;
      this.index = 0;
      this.muted = false;
      const el = document.createElement('div');
      el.className = 'ugc-player';
      el.setAttribute('role', 'dialog');
      el.setAttribute('aria-modal', 'true');
      el.setAttribute('aria-label', row.getAttribute('aria-label') || 'Customer videos');
      el.hidden = true;
      el.innerHTML = `
        <div class="ugc-player__backdrop"></div>
        <div class="ugc-player__stage">
          <div class="ugc-player__frame">
            <video playsinline preload="auto"></video>
            <span class="ugc-row__spinner ugc-player__spinner" aria-hidden="true"></span>
            <div class="ugc-player__top">
              <div class="ugc-player__progress" aria-hidden="true"><span></span></div>
              <div class="ugc-player__controls">
                <button type="button" class="ugc-player__btn" data-action="pause" aria-label="Pause video" aria-pressed="false">${ICON_PAUSE}</button>
                <button type="button" class="ugc-player__btn" data-action="sound" aria-label="Sound" aria-pressed="true">${ICON_SOUND}</button>
                <button type="button" class="ugc-player__btn" data-action="close" aria-label="Close videos">${ICON_CLOSE}</button>
              </div>
            </div>
            <span class="ugc-player__caption" aria-hidden="true"></span>
            <a class="ugc-player__product" href="#">
              <img alt="" width="56" height="56">
              <span class="ugc-player__product-text">
                <span class="ugc-player__product-name"></span>
                <span class="ugc-player__product-price"></span>
              </span>
              <span class="ugc-player__product-cta">Shop now</span>
            </a>
            <span class="ugc-player__hint" aria-hidden="true">Swipe up for more</span>
          </div>
          <button type="button" class="ugc-player__nav ugc-player__nav--prev" data-action="prev" aria-label="Previous video">${ICON_UP}</button>
          <button type="button" class="ugc-player__nav ugc-player__nav--next" data-action="next" aria-label="Next video">${ICON_DOWN}</button>
        </div>
        <p class="ugc-row__sr ugc-player__status" aria-live="polite"></p>`;
      document.body.appendChild(el);
      this.el = el;
      this.frame = el.querySelector('.ugc-player__frame');
      this.video = el.querySelector('video');
      this.bar = el.querySelector('.ugc-player__progress span');
      this.caption = el.querySelector('.ugc-player__caption');
      this.product = el.querySelector('.ugc-player__product');
      this.bind();
    }

    bind() {
      const { el, video } = this;
      el.addEventListener('click', (e) => {
        const action = e.target.closest('[data-action]')?.dataset.action;
        if (action === 'close') this.close();
        else if (action === 'next') this.step(1);
        else if (action === 'prev') this.step(-1);
        else if (action === 'sound') this.setMuted(!this.muted);
        else if (action === 'pause') this.togglePause();
        else if (e.target.closest('.ugc-player__backdrop, .ugc-player__stage') && !e.target.closest('.ugc-player__frame')) this.close();
        else if (e.target === video) this.togglePause();
      });
      this.product.addEventListener('click', (e) => {
        const card = this.cards[this.index];
        const info = this.row.productOf(card);
        track('ugc_product_click', {
          ugc_video: card.dataset.name,
          ugc_placement: this.row.placement,
          ugc_trigger: 'player',
          ugc_product: info?.title,
        });
        if (info?.onPage) {
          // Already on this product's page: close and take the visitor to the buy box.
          e.preventDefault();
          this.close();
          const buyBox = document.querySelector('[id^="product-info-"]') || document.querySelector('product-info, .product-info');
          buyBox?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
      el.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
        else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') { e.preventDefault(); this.step(1); }
        else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') { e.preventDefault(); this.step(-1); }
        else if (e.key === 'Tab') this.trapFocus(e);
      });
      video.addEventListener('ended', () => this.step(1, true));
      video.addEventListener('playing', () => this.el.classList.remove('is-buffering'));
      video.addEventListener('waiting', () => this.el.classList.add('is-buffering'));
      video.addEventListener('play', () => this.syncPause());
      video.addEventListener('pause', () => this.syncPause());

      // Wheel / trackpad on desktop: one gesture, one video.
      let wheelLock = 0;
      el.addEventListener('wheel', (e) => {
        e.preventDefault();
        const now = performance.now();
        if (now < wheelLock) { wheelLock = now + 250; return; }
        if (Math.abs(e.deltaY) < 30) return;
        wheelLock = now + 600;
        this.step(e.deltaY > 0 ? 1 : -1);
      }, { passive: false });

      // Vertical swipe on phones: the video follows the finger, then moves on or springs back.
      let start = null;
      this.frame.addEventListener('touchstart', (e) => {
        if (e.target.closest('button, a')) return;
        start = { y: e.touches[0].clientY, x: e.touches[0].clientX, t: performance.now() };
        this.frame.classList.add('is-dragging');
      }, { passive: true });
      this.frame.addEventListener('touchmove', (e) => {
        if (!start) return;
        const dy = e.touches[0].clientY - start.y;
        this.frame.style.setProperty('--drag', `${dy}px`);
      }, { passive: true });
      const end = (e) => {
        if (!start) return;
        const touch = e.changedTouches && e.changedTouches[0];
        const dy = touch ? touch.clientY - start.y : 0;
        const dx = touch ? touch.clientX - start.x : 0;
        const fast = Math.abs(dy) / Math.max(1, performance.now() - start.t) > 0.5;
        start = null;
        this.frame.classList.remove('is-dragging');
        this.frame.style.setProperty('--drag', '0px');
        if (Math.abs(dy) > Math.abs(dx) && (Math.abs(dy) > 80 || (fast && Math.abs(dy) > 30))) this.step(dy < 0 ? 1 : -1);
        else if (Math.abs(dx) > 90 && Math.abs(dx) > Math.abs(dy) * 1.5) this.close(); // sideways flick closes
      };
      this.frame.addEventListener('touchend', end, { passive: true });
      this.frame.addEventListener('touchcancel', end, { passive: true });

      window.addEventListener('popstate', () => { if (this.isOpen) this.close(true); });
    }

    open(index, time, fromKeyboard = false) {
      this.isOpen = true;
      this.returnFocus = document.activeElement;
      this.el.hidden = false;
      document.documentElement.classList.add('ugc-player-open');
      history.pushState({ ugcPlayer: true }, '');
      requestAnimationFrame(() => this.el.classList.add('is-open'));
      this.setMuted(false);
      this.show(index, time);
      // Focus moves into the player; the ring only shows for keyboard users.
      const closeButton = this.el.querySelector('[data-action="close"]');
      closeButton.focus({ preventScroll: true, focusVisible: fromKeyboard });
      if (!this.hintShown) {
        this.hintShown = true;
        this.el.classList.add('show-hint');
        setTimeout(() => this.el.classList.remove('show-hint'), 2600);
      }
    }

    close(fromHistory = false) {
      if (!this.isOpen) return;
      this.isOpen = false;
      cancelAnimationFrame(this.frameLoop);
      this.video.pause();
      this.el.classList.remove('is-open');
      document.documentElement.classList.remove('ugc-player-open');
      setTimeout(() => {
        if (this.isOpen) return;
        this.el.hidden = true;
        this.video.removeAttribute('src');
        this.video.load();
      }, 200);
      if (!fromHistory && history.state && history.state.ugcPlayer) history.back();
      this.returnFocus?.focus?.({ preventScroll: true });
      this.row.resumeFrom(this.index);
    }

    step(direction, auto = false) {
      const n = this.cards.length;
      if (n < 2 && !auto) return;
      if (n < 2) { this.video.currentTime = 0; this.video.play().catch(() => {}); return; }
      const next = (((this.index + direction) % n) + n) % n;
      this.frame.classList.add(direction > 0 ? 'is-leaving-up' : 'is-leaving-down');
      setTimeout(() => {
        this.frame.classList.remove('is-leaving-up', 'is-leaving-down');
        this.show(next, 0, direction);
      }, 160);
    }

    async show(index, time = 0, direction = 0) {
      this.index = index;
      const card = this.cards[index];
      const source = card.querySelector('video');
      const n = this.cards.length;
      this.el.querySelector('.ugc-player__status').textContent = `Video ${index + 1} of ${n}`;
      this.el.classList.add('is-buffering');
      if (direction) {
        this.frame.classList.add(direction > 0 ? 'is-entering-up' : 'is-entering-down');
        requestAnimationFrame(() => requestAnimationFrame(() => this.frame.classList.remove('is-entering-up', 'is-entering-down')));
      }

      // Product card.
      const info = this.row.productOf(card);
      this.product.hidden = !info;
      if (info) {
        this.product.href = info.url;
        this.product.querySelector('img').src = info.image || '';
        this.product.querySelector('.ugc-player__product-name').textContent = info.title || '';
        this.product.querySelector('.ugc-player__product-price').textContent = info.price || '';
      }

      // Captions (only videos without burned-in ones have cues).
      this.phrases = card.ugcCues ? toPhrases(card.ugcCues, 30) : null;
      this.shownPhrase = null;
      this.caption.classList.remove('is-shown');

      // Video: the same file chooser as the row, allowing full HD within the connection budget.
      const gen = (this.gen = (this.gen || 0) + 1);
      const url = await pickFile(source, 1080, 8e6); // full screen: 720p (as Tolstoy) unless the connection is slow
      if (gen !== this.gen || !this.isOpen) return;
      this.video.src = url;
      this.video.muted = this.muted;
      if (time) {
        this.video.addEventListener('loadedmetadata', () => { this.video.currentTime = time; }, { once: true });
      }
      this.video.play().catch(() => {
        // Sound blocked (rare after a tap): play muted instead.
        this.setMuted(true);
        this.video.play().catch(() => {});
      });
      this.row.trackOnce(`player:${index}`, 'ugc_video_start', {
        ugc_video: card.dataset.name,
        ugc_placement: this.row.placement,
        ugc_trigger: 'player',
        ugc_position: index + 1,
      });
      this.prefetch(index + 1);
      cancelAnimationFrame(this.frameLoop);
      const loop = () => {
        this.tick();
        if (this.isOpen) this.frameLoop = requestAnimationFrame(loop);
      };
      this.frameLoop = requestAnimationFrame(loop);
    }

    // Fetch the next video's stream index so it starts at once.
    prefetch(index) {
      const card = this.cards[index % this.cards.length];
      const hls = card?.querySelector('video').dataset.hls;
      if (hls) readIndex(hls);
    }

    tick() {
      const { video } = this;
      if (video.duration) this.bar.style.transform = `scaleX(${Math.min(1, video.currentTime / video.duration)})`;
      if (!this.phrases) return;
      const t = video.currentTime;
      const phrase = this.phrases.find((p) => t >= p.start && t < p.end) || null;
      if (phrase === this.shownPhrase) return;
      this.shownPhrase = phrase;
      if (phrase) this.caption.textContent = phrase.text;
      this.caption.classList.toggle('is-shown', Boolean(phrase));
    }

    setMuted(muted) {
      this.muted = muted;
      this.video.muted = muted;
      const button = this.el.querySelector('[data-action="sound"]');
      button.innerHTML = muted ? ICON_MUTED : ICON_SOUND;
      button.setAttribute('aria-pressed', String(!muted));
    }

    togglePause() {
      if (this.video.paused) this.video.play().catch(() => {});
      else this.video.pause();
    }

    syncPause() {
      const paused = this.video.paused;
      const button = this.el.querySelector('[data-action="pause"]');
      button.innerHTML = paused ? ICON_PLAY_SMALL : ICON_PAUSE;
      button.setAttribute('aria-label', paused ? 'Play video' : 'Pause video');
      button.setAttribute('aria-pressed', String(paused));
      this.el.classList.toggle('is-paused', paused);
    }

    trapFocus(e) {
      const items = [...this.el.querySelectorAll('button, a[href]')].filter((item) => !item.hidden && item.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }

  customElements.define('ugc-video-row', UgcVideoRow);
})();
